import { getIndustries, IIndustry } from "@/api/industries";
import { TextField } from "@/components/shared/TextField";
import { Autocomplete } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface IndustryAutocompleteProps {
    value: { id: number } | null;
    onChange: (industry: IIndustry | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

export const IndustryAutocomplete = ({
    value,
    onChange,
    label = "Industry",
    required = false,
    disabled = false,
    error = false,
    helperText,
}: IndustryAutocompleteProps) => {
    const { data: industries, isPending } = useQuery({
        queryKey: ["industries"],
        queryFn: getIndustries,
        staleTime: 5 * 60 * 1000,
    });

    const industryOptions = useMemo(() => industries ?? [], [industries]);

    const selectedIndustry = useMemo(
        () => industryOptions.find((ind) => ind.id === value?.id) || null,
        [value?.id, industryOptions]
    );

    return (
        <Autocomplete
            options={industryOptions}
            value={selectedIndustry}
            getOptionLabel={(option) => `${option.name} (NAIC: ${option.naic})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => onChange(newValue)}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="Select an industry"
                    required={required}
                    error={error}
                    helperText={helperText}
                />
            )}
            size="small"
            autoComplete
            loading={isPending}
            noOptionsText={isPending ? "Loading..." : "No industries found"}
            loadingText="Loading industries..."
        />
    );
};
